import { api } from "@/api/client";
import { CopyButton } from "@/components/CopyButton";
import { PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { formatInt } from "@/lib/format";
import { splitPromptSections } from "@/lib/promptSections";
import { depthLabel, depthValue } from "@/lib/reasoningDepth";

// Prompts change only with a deploy (PROMPT_VERSION) — a slow poll suffices.
const POLL_MS = 300_000;

const MONO = "font-family: var(--font-mono); font-size: 13px;";
const PROMPT_TEXT =
  "white-space: pre-wrap; overflow-wrap: anywhere; " +
  "font-family: var(--font-mono); font-size: 13px; line-height: 1.6; " +
  "margin: 8px 0 4px; padding: 12px; background: rgba(0, 45, 165, 0.04); " +
  "border-radius: var(--radius-s);";

export function PromptsView() {
  const { data, loading, error, lastFetched, refetch } = usePoll((s) => api.prompts(s), [], {
    intervalMs: POLL_MS,
  });

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness lastFetched={lastFetched} loading={loading} error={error} onRefresh={refetch} />
        {data && (
          <span class="muted">
            {t("prompts.version")} <span style={MONO}>{data.prompt_version}</span>
          </span>
        )}
      </div>
      <PanelHelp k="prompts.overview" />

      {data?.modes.map((m) => (
        <section class="card" key={m.mode}>
          <div style="display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap;">
            <h2 style="margin: 0;">{m.label}</h2>
            <span class="muted" style={MONO}>
              {m.mode}
            </span>
            {!m.enabled && <span class="pill degraded">{t("prompts.disabled")}</span>}
            {/* The whole canonical text, not the trimmed sections below. */}
            <div style="margin-left: auto; display: flex; align-items: baseline; gap: 8px;">
              <CopyButton
                text={m.system_prompt}
                label={t("prompts.copy")}
                done={t("prompts.copied")}
                failed={t("prompts.copyFailed")}
              />
            </div>
          </div>
          <div class="muted" style="margin: 6px 0 2px;">
            {t("prompts.model")} <span style={MONO}>{m.model}</span>
            {" · "}
            {depthLabel(m)} <span style={MONO}>{depthValue(m)}</span>
            {" · "}
            {t("prompts.chars", { n: formatInt(m.char_count) })}
          </div>
          <p class="muted" style="margin: 4px 0 10px;">
            {m.note}
          </p>
          {splitPromptSections(m.system_prompt).map((s, i) => (
            <details key={`${m.mode}-${i}`} open={s.heading === ""}>
              <summary style="cursor: pointer; font-weight: 600;">
                {s.heading || t("prompts.preamble")}
              </summary>
              <div style={PROMPT_TEXT}>{s.text.trim()}</div>
            </details>
          ))}
        </section>
      ))}
    </div>
  );
}
